import React from "react";
import "../../../AllComponentCSS.css";
import CodeCopyBlock from "../../../CodeCopyBlock";

const Fonts_Shorthand_Property = () => {
  return (
    <div className="tutorial-container">
      {/* Title */}
      <h1 className="tutorial-title">✍️ CSS Fonts Shorthand Property</h1>

      {/* Intro Box */}
      <div
        style={{
          background: "linear-gradient(135deg, #d1fae5, #bbf7d0)",
          padding: "1rem",
          borderRadius: "12px",
          marginBottom: "2rem",
          textAlign: "center",
        }}
      >
        <h2 style={{ color: "#166534", marginBottom: "0.5rem" }}>
          ✨ What is the Font Shorthand Property?
        </h2>
        <p style={{ fontSize: "1.1rem", lineHeight: "1.6" }}>
          The <code>font</code> shorthand property lets you set several font
          properties in one declaration instead of writing them separately. It
          helps keep your CSS clean and compact.
        </p>
      </div>

      {/* Explanation */}
      <div className="tutorial-section">
        <h2 className="section-title">📖 Properties Combined in Shorthand</h2>
        <p>
          The <code>font</code> shorthand can include the following properties:
        </p>
        <ul className="tutorial-list">
          <li>
            <code>font-style</code> (normal, italic, oblique)
          </li>
          <li>
            <code>font-variant</code> (normal, small-caps)
          </li>
          <li>
            <code>font-weight</code> (normal, bold, 100–900)
          </li>
          <li>
            <code>font-size</code> (required)
          </li>
          <li>
            <code>line-height</code> (optional, after font-size with a{" "}
            <code>/</code>)
          </li>
          <li>
            <code>font-family</code> (required)
          </li>
        </ul>
      </div>

      {/* Syntax Rule */}
      <div className="tutorial-section">
        <h2 className="section-title">📋 Syntax Order</h2>
        <p>The correct order of values is:</p>
        <pre className="code-block">{`font: [style] [variant] [weight] [size]/[line-height] [family];`}</pre>
        <p>
          <strong>Note:</strong> <code>font-size</code> and{" "}
          <code>font-family</code> are <strong>required</strong>, while others
          are optional.
        </p>
      </div>

      {/* Examples */}
      <div className="tutorial-section">
        <h2 className="section-title">👨‍💻 Examples</h2>
        <CodeCopyBlock
          code={`/* Normal separate properties */
p {
  font-style: italic;
  font-weight: bold;
  font-size: 16px;
  line-height: 1.5;
  font-family: "Georgia", serif;
}

/* Using shorthand */
p {
  font: italic bold 16px/1.5 "Georgia", serif;
}`}
        />

        {/* Live Preview */}
        <p
          style={{
            font: 'italic bold 16px/1.5 "Georgia", serif',
            background: "#f9fafb",
            padding: "8px",
            borderRadius: "6px",
          }}
        >
          This text uses the <strong>font shorthand property</strong>.
        </p>
      </div>

      {/* Notes & Best Practices */}
      <div className="tutorial-section">
        <h2 className="section-title">💡 Notes & Best Practices</h2>
        <ul className="tutorial-list">
          <li>
            Always include <code>font-size</code> and <code>font-family</code>,
            otherwise the shorthand won’t work.
          </li>
          <li>
            If you skip optional values, they revert to their defaults (e.g.,{" "}
            <code>font-style: normal</code>).
          </li>
          <li>
            Use quotes for multi-word font families (e.g.,{" "}
            <code>"Times New Roman"</code>).
          </li>
          <li>
            Shorthand makes CSS cleaner, but be careful: it will override all
            font-related properties, even ones you didn’t set.
          </li>
        </ul>
      </div>

      {/* Next Steps */}
      <div className="tutorial-section">
        <h2 className="section-title">🚀 Next Steps</h2>
        <p>
          Now that you know the shorthand property, explore{" "}
          <a href="/css/font-size" className="learn-more">
            CSS Font Size
          </a>{" "}
          and{" "}
          <a href="/css/font-weight" className="learn-more">
            CSS Font Weight
          </a>{" "}
          for more control over text appearance.
        </p>
      </div>
    </div>
  );
};

export default Fonts_Shorthand_Property;
